// Native <dialog>-based scaffolding for every dialog the embed
// script pops. Two variants, sharing one set of scaffolding:
//
//   - **Modal** — fullscreen on phones, centered card on larger
//     viewports. Uses dialog.showModal(), backdrop, body-scroll
//     lock. Consumers: renderModal (multi-org info), donationModal
//     (donate widget in a dialog, with the closeOutside chrome).
//
//   - **Corner** (non-modal) — fixed-positioned in a viewport
//     corner, no backdrop, no body-scroll lock. Uses dialog.show().
//     Consumer: the upcoming abandoned-cart dialog (renderDialog).
//
// Layered:
//
//   - Low-level helpers used by both variants: createDialog (DOM +
//     shadow root + close button), closeDialog (fade-out lifecycle).
//   - Per-variant style + show pairs: injectModalStyles /
//     showModalDialog vs injectCornerStyles / showCornerDialog.
//   - Generalized renderers: renderModal / renderDialog. Both inject
//     the right styles, build the scaffold, mount an iframe, and
//     show. The cross-frame dispatchers (openModal, openDialog)
//     route to these or send a channel message to the parent frame
//     to render on the donor's behalf.
//
// Phase B context: the modal infrastructure is being built up to
// host modals other than the donate / search widgets. See
// project_cross_frame_modals memory for the roadmap.
//
// dialog-polyfill brings <dialog> + showModal() down to Safari <15.4
// so MiniDonate / Launch / Phase B's modal system can target the
// widget surface's Safari 14 floor (CLAUDE.md → Browser Support).
// Loaded conditionally from /embed/dialog_polyfill.js when the
// browser lacks native showModal — modern browsers pay zero bytes.
// New entry points that use dialog.js and want Safari 14 support
// must call polyfillDialog(origin) at init time; surfaces with a
// higher floor (v7, etc.) skip the call. See polyfillDialog below.
//
// The close button + loading spinner live inside a closed shadow
// root on the body element. Partner page CSS can't reach across the
// shadow boundary, so theme stylesheets (most painfully the ones on
// our largest MiniDonate partner) can't override their styling. The
// iframe / widget-div the caller appends is rendered via a <slot>
// inside the shadow root and stays in light DOM — MutationObserver
// code (createWidget's onElementsAvailable) still sees it.

import { SOURCE } from 'src/utils/messages/protocol'

import {
  FADE_DURATION,
  cornerStyles,
  modalStyles,
  takeoverStyles,
  shadowHTML,
} from './dialogStyles'

// If the browser lacks native <dialog>, fetch the polyfill from
// `${origin}/embed/dialog_polyfill.js`. The script tag is async;
// by the time a user clicks a modal trigger (seconds later) the
// polyfill is parsed and window.dialogPolyfill is populated. Race
// window between injection and the very-first modal open is
// theoretical — never occurs in practice.
//
// Modern browsers (Safari 15.4+, evergreen everything else) short-
// circuit on the feature check and pay zero network bytes. Surfaces
// that don't need to support Safari 14 (v7, etc.) shouldn't call
// this at all.
//
// Callers today:
//   - Widgets.js#init — host-page entry (widget.js pack)
//   - donate_v6/openModalTriggers.js — donate widget iframe entry
//
// `nonce` is the host page's CSP nonce (harvested from the embed
// script tag via scriptDetails().nonce) so the injected polyfill
// script can pass a nonce-only CSP. Undefined for partners not
// running nonce-CSP.
//
// Idempotent: repeat calls in the same window are a no-op. Browsers
// don't dedupe identical <script src> URLs the way they do imports,
// so a second injection would re-fetch + re-execute the polyfill.
let polyfillInjected = false
export const polyfillDialog = (origin, nonce) => {
  if (polyfillInjected) return
  if (typeof HTMLDialogElement !== 'undefined' &&
      typeof HTMLDialogElement.prototype.showModal === 'function') return

  polyfillInjected = true
  const polyfillScript = document.createElement('script')
  polyfillScript.src = `${origin}/embed/dialog_polyfill.js`
  if (nonce) polyfillScript.nonce = nonce
  polyfillScript.async = true
  document.head.appendChild(polyfillScript)
}

let modalStylesInjected = false
export const injectModalStyles = () => {
  if (modalStylesInjected) return
  modalStylesInjected = true
  const style = document.createElement('style')
  style.textContent = modalStyles
  document.head.appendChild(style)
}

let cornerStylesInjected = false
export const injectCornerStyles = () => {
  if (cornerStylesInjected) return
  cornerStylesInjected = true
  const style = document.createElement('style')
  style.textContent = cornerStyles
  document.head.appendChild(style)
}

// Styles for the 3DS full-screen takeover (createWidget's
// wireModalTakeover). Injected lazily on the first takeover so pages
// whose donors never hit a 3DS challenge pay nothing.
let takeoverStylesInjected = false
export const injectTakeoverStyles = () => {
  if (takeoverStylesInjected) return
  takeoverStylesInjected = true
  const style = document.createElement('style')
  style.textContent = takeoverStyles
  document.head.appendChild(style)
}

// Dialogs whose close is currently suppressed — set while a donation is
// mid-flight so the donor can't dismiss the modal (close button,
// backdrop, or Esc) while their payment is processing. A WeakSet keyed
// on the dialog element rather than a DOM attribute, so a host page's
// CSS/JS can neither see nor toggle it. closeDialog bails while a dialog
// is in the set.
const lockedDialogs = new WeakSet()

// Force-release fallback. If the widget that locked the dialog never
// sends `unlockModal` (crashed mid-submit, tab discarded, network
// died), auto-unlock after this long so a stalled donation can't leave
// the donor permanently trapped in the modal. Set above the slow tail
// of legitimate processing — a drawn-out 3DS (covered by the 3DS
// takeover anyway) or a PayPal/ACH/order-create call on a bad mobile
// connection — so it force-releases only when the widget has really
// gone silent, while still bounding the worst case to a couple minutes
// (the donor can reload regardless).
export const LOCK_SAFETY_TIMEOUT = 120_000
const lockTimers = new WeakMap()

// The dialog's close button lives in the body's (open) shadow root.
const closeButtonOf = dialog =>
  dialog.querySelector('.plg-dialog-body')?.shadowRoot?.querySelector('.close')

// Transient "not now" shake when a close is attempted on a locked
// dialog via Esc or backdrop (the close button is disabled, so those
// are the only paths that reach here). Strip + reflow + re-add so a
// rapid repeat attempt replays the animation. CSS in dialogStyles.js.
const NUDGE_CLASS = 'plg-dialog-locked-nudge'
const nudgeLocked = dialog => {
  const body = dialog.querySelector('.plg-dialog-body')
  if (!body) return
  body.classList.remove(NUDGE_CLASS)
  void body.offsetWidth // reflow so re-adding restarts the animation
  body.classList.add(NUDGE_CLASS)
}

// Suppress every close path (button / backdrop / Esc) for this dialog
// until unlockDialog runs or the safety timer fires. Idempotent — a
// repeat lock just restarts the safety timer. Also disables the close
// button as the persistent "can't close now" affordance. Wired to the
// donate widget's `lockModal` channel message in Widgets.js.
export const lockDialog = dialog => {
  if (!dialog) return
  lockedDialogs.add(dialog)
  const button = closeButtonOf(dialog)
  if (button) button.disabled = true
  clearTimeout(lockTimers.get(dialog))
  lockTimers.set(dialog, setTimeout(() => unlockDialog(dialog), LOCK_SAFETY_TIMEOUT))
}

// Re-enable closing (+ the close button). Wired to the widget's
// `unlockModal` message and also called by the safety timer above.
// Idempotent.
export const unlockDialog = dialog => {
  if (!dialog) return
  lockedDialogs.delete(dialog)
  const button = closeButtonOf(dialog)
  if (button) button.disabled = false
  clearTimeout(lockTimers.get(dialog))
  lockTimers.delete(dialog)
}

export const closeDialog = dialog => {
  if (!dialog || !dialog.open || !dialog.classList.contains('open')) return

  // Held open while a donation is processing (see lockDialog). All three
  // user close paths — close button, backdrop, Esc (cancel) — funnel
  // through here, so this one guard blocks every way of dismissing the
  // dialog mid-payment. Per-dialog, so only the locked donation dialog
  // is held; other dialogs (abandoned-cart corner, etc.) close normally.
  // The safety timer in lockDialog guarantees this can't trap the donor
  // permanently if the release never arrives.
  if (lockedDialogs.has(dialog)) return

  // Dialogs carrying the [data-cached] attr belong to renderModal's
  // per-id cache and survive close — `dialog.close()` removes the
  // [open] attribute so the fade-out CSS runs, but the node stays in
  // the DOM and a future renderModal({ id }) call calls showModal()
  // on it again to reopen without reloading the iframe. Using a data
  // attr (vs. a class) keeps the marker out of any reach a host
  // page's stylesheet might have.
  const isCached = dialog.hasAttribute('data-cached')
  dialog.classList.remove('open')
  // Scroll-lock release — the Safari-14 fallback for the injected
  // `html:has(.plg-dialog[open])` rule (dialogStyles.js), which handles
  // this on browsers that support :has(). Only for modal dialogs
  // (corner dialogs never locked), and only once NO other modal is
  // still open: embed modals can stack (a donate form + a multi-org
  // info modal launched from inside it), so closing the inner one must
  // not unlock while the outer is up. Checks the `.open` CLASS — removed
  // synchronously just above for THIS dialog, but still present on any
  // other open modal — rather than the `[open]` attribute, which this
  // dialog keeps until dialog.close() runs in the fade-out timeout below
  // (so `[open]` would still count the closing dialog and never release).
  if (dialog.matches('.plg-dialog') && !document.querySelector('.plg-dialog.open'))
    document.documentElement.style.overflow = ''
  setTimeout(() => {
    dialog.close()
    if (!isCached) dialog.remove()
  }, FADE_DURATION)
}

// Build the dialog scaffold + body container. Caller fills the
// returned `content` element with iframe / widget div / whatever;
// it appears in the shadow root via a <slot>, so the caller's
// content stays in light DOM but renders inside the protected
// shadow layout.
//
// ariaLabel defaults to "Dialog"; pass something more descriptive
// when the content has a known purpose ("Donate", "NPO info", etc.)
// — and localize it once Phase B's i18n pass lands.
//
// `closeOutside` is the donate-form-in-modal escape hatch. The
// donate widget's own header doesn't have a slot for an in-body
// close button without colliding with content. At ≥ 375px the
// outside variant pulls the close button above the body into the
// backdrop area, smaller and with a visible "Close" label.
// Sub-375px stays inside (no room to overhang). Modal-only — corner
// dialogs ignore the flag (no backdrop area to overhang into).
//
// `scrollWithin` is the portal-mount picker escape hatch. Default
// modals grow with their iframe content (resizeIframe sets
// frameElement.style.height to match the iframe document); the
// scrollWithin variant caps the body + iframe at viewport height
// (or viewport minus margins, at ≥ 375px) so the iframe document
// scrolls internally beneath a sticky picker header instead. Set
// once at createDialog time as a data attribute on the body; the
// CSS in dialogStyles.js keys off it. Modal-only.
//
// `className` selects the variant: `'plg-dialog'` (default) for
// modal styling, `'plg-corner-dialog'` for the non-modal corner
// variant. closeDialog's scroll-unlock branch and the per-variant
// CSS both key off this class.
export const createDialog = ({
  ariaLabel = 'Dialog',
  closeOutside = false,
  scrollWithin = false,
  className = 'plg-dialog',
  maxWidth,
} = {}) => {
  const dialog = document.createElement('dialog')
  // If the polyfill loaded (only happens on Safari <15.4), hook it
  // onto this dialog. Modern browsers don't load the polyfill so
  // window.dialogPolyfill is undefined and we skip registration.
  window.dialogPolyfill?.registerDialog(dialog)
  dialog.className = className
  dialog.setAttribute('aria-label', ariaLabel)

  const body = document.createElement('div')
  body.className = 'plg-dialog-body'
  body.tabIndex = -1
  body.setAttribute('autofocus', '')
  if (closeOutside) body.dataset.closeOutside = ''
  if (scrollWithin) body.dataset.scrollWithin = ''

  // A locked dialog (donation processing) can't be closed: the button
  // is disabled, so only Esc + backdrop reach here — nudge instead of
  // closing. closeDialog also self-guards, so programmatic callers stay
  // safe too; this branch is what turns a rejected USER close into
  // feedback rather than a silent no-op.
  const attemptClose = () => {
    if (lockedDialogs.has(dialog)) return nudgeLocked(dialog)
    closeDialog(dialog)
  }

  const shadow = body.attachShadow({ mode: 'open' })
  shadow.innerHTML = shadowHTML
  shadow.querySelector('.close')
    .addEventListener('click', attemptClose)

  dialog.appendChild(body)

  applyMaxWidth(dialog, maxWidth)

  dialog.addEventListener('cancel', e => {
    e.preventDefault()
    attemptClose()
  })

  dialog.addEventListener('click', e => {
    if (e.target === dialog) attemptClose()
  })

  document.body.appendChild(dialog)
  // `content` is the body itself; callers do
  // `content.appendChild(iframe)` and the iframe goes into light
  // DOM as a child of the body, rendered into the <slot> in the
  // shadow root.
  return { dialog, content: body }
}

export const showModalDialog = dialog => {
  document.documentElement.style.overflow = 'hidden'
  dialog.showModal()
  void dialog.offsetHeight // force reflow so the .open transition fires
  dialog.classList.add('open')
}

// Non-modal show. No body-scroll lock; uses native dialog.show()
// which doesn't dismiss on Escape (per spec). The close button in
// the shadow root + caller-initiated dismissal carry the close UX.
export const showCornerDialog = dialog => {
  dialog.show()
  void dialog.offsetHeight
  dialog.classList.add('open')
}

// Languages we localize into that read right-to-left. Resolved from
// the locale tag's primary subtag (e.g. 'ar-EG' → 'ar') so regional
// variants get the right direction automatically. Kept as a small
// hand-curated set rather than `new Intl.Locale(...).textInfo` —
// textInfo lands in Safari 16.4+ and our widgets need to work down
// to Safari 14 via the dialog-polyfill.
const RTL_LOCALES = new Set([ 'ar', 'fa', 'he', 'ps', 'sd', 'ug', 'ur', 'yi' ])

const isRtl = locale =>
  !!locale && RTL_LOCALES.has(locale.toLowerCase().split('-')[0])

// `src` allowlist for renderModal. The channel host's CONNECT-time
// origin check already bounds who can send openModal over the wire
// to pledge.to-origin iframes, but the src payload itself is opaque
// to the channel — and openModalTriggers' `dataset.plgModalSrc ||
// trigger.href` flows through here too. Defense in depth against a
// `javascript:` URL slipping in: reject anything that isn't http(s)
// or a same-origin relative path before iframe.src is assigned.
const isAllowedSrc = src =>
  typeof src === 'string' &&
  (src.startsWith('https://') || src.startsWith('http://') || src.startsWith('/'))

// Generalized modal renderer: pops a dialog hosting an iframe at the
// given src. Used by:
//   - Widgets.js#init's `openModal` channel listener (host-side path
//     when an iframe sends the open request over the channel)
//   - openModal.js (the iframe-side dispatcher) when picking the
//     top-frame or in-iframe-fallback runtime path
//
// For donate-widget-specific opens (today's MiniDonate / Launch + the
// [data-plg-action="donate"] trigger), donationModal.js is the
// specialized caller — it injects a widget-class div instead of a
// plain iframe so createWidget's pipeline (httpsOnly fallback, locale
// detection, channel registration, height sync) takes over.
//
// `locale` controls the close button's edge (right in LTR, left in
// RTL) via `dir` on the dialog element — the shadow root inherits
// direction from its host, and inset-inline-end resolves correctly.
// Threaded through the channel payload by the iframe-side openModal
// dispatcher, so the host renders chrome that matches the iframe's
// localized content even when the partner page is in a different
// locale.
//
// `height` defaults to 192px (matches the body's min-height floor)
// so the dialog opens small, the spinner is visible while the iframe
// loads, and resizeIframe.js — which the modal-content layouts at
// app/views/layouts/widgets/*.html.erb include — grows the iframe
// to its actual content height once it paints. That gives a smooth
// small-to-correct-size grow instead of the old large-to-smaller
// jump (where 80vh shrunk to ~400px the moment resizeIframe ran).
// Callers can pass an explicit height for content that isn't
// auto-resizing.
//
// Returns `{ dialog, iframe }` on success — `dialog` so callers can
// close it programmatically, `iframe` so the channel-routed openModal
// path (Widgets.js) can register it with the host for height sync.
// The other two openModal paths get height sync via resizeIframe.js's
// same-origin frameElement branch and don't need the iframe handle.
//
// Returns `null` and warns if `src` fails the allowlist; callers
// destructuring the return must guard.
//
// Optional `id` keys the per-modal cache: a future renderModal({ id })
// call with the same id reuses the existing dialog (just reopens it)
// instead of building a fresh one — the iframe stays loaded, so re-
// opens are instant and we don't re-fetch the modal content. The id
// is opaque here; convention is `<type>:<key>` so a future wildcard
// interceptor can match by type. If a later call passes a different
// src for the same id we update iframe.src in place and warn — the
// callers are inconsistent and only one of them can be right.
const modalCache = new Map()

export const renderModal = ({
  id, src, ariaLabel, locale, height = '192px', maxWidth, scrollWithin,
} = {}) => {
  if (!isAllowedSrc(src)) {
    console.warn('renderModal: rejecting src that is not http(s) or a relative path', src)
    return null
  }

  if (id && modalCache.has(id)) {
    const cached = modalCache.get(id)
    if (cached.src !== src) {
      console.warn(
        `renderModal: cached modal '${id}' was opened with src '${cached.src}' `
        + `but the next call passed '${src}'. Updating in place — `
        + 'the call sites are inconsistent and one of them is wrong.'
      )
      cached.iframe.src = src
      cached.src = src
    }
    applyMaxWidth(cached.dialog, maxWidth)
    showModalDialog(cached.dialog)
    return { dialog: cached.dialog, iframe: cached.iframe }
  }

  injectModalStyles()
  const { dialog, content } = createDialog({ ariaLabel, scrollWithin })
  if (locale) dialog.setAttribute('dir', isRtl(locale) ? 'rtl' : 'ltr')
  applyMaxWidth(dialog, maxWidth)

  const iframe = document.createElement('iframe')
  // `name` set before `src` so the browsing context's window.name is
  // populated by the time the iframe document loads. The opener-side
  // openPortalModal uses `window.parent.frames[id]` to reach the
  // modal iframe across same-origin sibling boundaries (no cross-
  // origin parent.document access needed). Safe for non-portal
  // modals — name is just ignored when nothing looks it up.
  if (id) iframe.name = id
  iframe.src = src
  iframe.style.height = height
  content.appendChild(iframe)

  if (id) {
    // closeDialog reads this attr to skip dialog.remove() so the
    // node + iframe survive close → reopen. A data attribute rather
    // than a class so host-page stylesheets can't accidentally style
    // it via a generic `.cached` rule.
    dialog.dataset.cached = ''
    modalCache.set(id, { dialog, iframe, src })
  }

  showModalDialog(dialog)
  return { dialog, iframe }
}

// Both the v6 (BS5) and v7 (TW4) widget themes set their root at the
// browser-default 16px, so a `rem` width is meant against that base.
// renderModal, though, mounts the dialog wherever the embed script
// runs — on a partner page whose root font-size we don't control, a
// `rem` would resolve against *their* base and stretch/shrink the
// modal. Convert rem → px up front so the width is the same physical
// size regardless of the host page. Callers pass rem for consistency;
// non-rem values (px, %, etc.) pass through untouched. The local
// renderers (injectLocalPortalModal) skip this — they mount inside
// the widget frame where `rem` already resolves against our 16px.
const REM_PX = 16
const remToPx = value => {
  if (typeof value !== 'string') return value
  const match = value.match(/^([\d.]+)rem$/)
  return match ? `${parseFloat(match[1]) * REM_PX}px` : value
}

// Apply a per-open max-width override via the --plg-dialog-max-width
// custom property. The dialogStyles modal-body width rule reads it
// with a 640px fallback, so a missing maxWidth leaves the default
// alone. Inheritance flows from the dialog element down into the
// body where the rule matches.
const applyMaxWidth = (dialog, maxWidth) => {
  if (maxWidth) dialog.style.setProperty('--plg-dialog-max-width', remToPx(maxWidth))
}

// Test-only — clears the renderModal id cache so specs can run with
// fresh state. Production code never needs to call this; reopen-
// after-close is the entire point of the cache.
export const __resetModalCacheForTests = () => modalCache.clear()

// Non-modal counterpart of renderModal. Pops a corner-positioned
// dialog hosting an iframe at the given src. No backdrop, no body
// scroll lock — coexists with the host page rather than taking it
// over. Used by the abandoned-cart consumer today; can host any
// passive-notification iframe.
//
// `position` ∈ { 'bottom-right' (default), 'bottom-left',
// 'top-right', 'top-left' } controls which viewport corner the
// dialog sits in. `height` defaults to a small fixed value so the
// dialog can grow on iframe load via resizeIframe.js the same way
// the modal variant does (callers may override).
//
// Returns `{ dialog, iframe }` on success; `null` (with a warn) on
// allowlist rejection.
export const renderDialog = ({
  src,
  ariaLabel,
  locale,
  height = '128px',
  position = 'bottom-right',
  background,
} = {}) => {
  if (!isAllowedSrc(src)) {
    console.warn('renderDialog: rejecting src that is not http(s) or a relative path', src)
    return null
  }
  injectCornerStyles()
  const { dialog, content } = createDialog({
    ariaLabel,
    className: 'plg-corner-dialog',
  })
  dialog.dataset.position = position
  if (locale) dialog.setAttribute('dir', isRtl(locale) ? 'rtl' : 'ltr')
  // Override the default white body bg with caller-supplied color so
  // a slow-loading iframe doesn't flash white against an eventually-
  // dark iframe content. Body bg shows through the iframe while its
  // own document is still loading (the dialog body is light DOM
  // beneath the slotted iframe).
  if (background) content.style.background = background

  const iframe = document.createElement('iframe')
  iframe.src = src
  iframe.style.height = height
  content.appendChild(iframe)

  // Iframe-initiated close: the iframe content posts an FSA-shaped
  // closeDialog message to its parent (this window) to dismiss the
  // dialog. Used by the abandoned-cart "Remind me later" / "No,
  // thanks" buttons. Source-checked against this specific iframe so
  // unrelated postMessages from elsewhere on the page can't dismiss
  // our dialog; meta.source is checked to filter out unrelated
  // vendor postMessages that happen to come from the same iframe.
  const onMessage = e => {
    if (e.source !== iframe.contentWindow) return
    if (e.data?.meta?.source !== SOURCE) return
    if (e.data?.type !== 'closeDialog') return
    closeDialog(dialog)
  }
  window.addEventListener('message', onMessage)
  dialog.addEventListener('close', () =>
    window.removeEventListener('message', onMessage), { once: true })

  showCornerDialog(dialog)
  return { dialog, iframe }
}
